import React, { type JSX } from 'react';

import { useTheme } from '@mui/material/styles';
import { CloneWidgetModal } from '../WidgetList';
import { useWidgetConfigHandlers } from '../common';
import { AppBar, Button, CircularProgress, Stack, Toolbar, Typography } from '@mui/material';
import { CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import { useConfirm } from '@fiji/common/src/hooks';
import { SaveOverrideModal } from './SaveOverrideModal';
import { useTypedSelector } from '@fiji/common';
import { getWidgetTypeValidationConfig } from '@fiji/common/src/utils/helpers';

const hasSourceChanged = (
    savedSourceId: string | null | undefined,
    selectedSourceId: string | null | undefined
): boolean => (savedSourceId ?? null) !== (selectedSourceId ?? null);

// Module-level preference checkers — extracted from preferenceHelper to reduce CCM
const checkAggregatedTrendsPrefs = (data: any, config: any): boolean =>
    data?.unitType !== config?.unitType ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.viewTitleBar !== config?.viewTitleBar ||
    data?.type !== config?.type ||
    data?.selectedTimePeriod !== config?.selectedTimePeriod;
const checkLoadTrendsPrefs = (data: any, config: any): boolean =>
    data?.viewAllButton !== config?.viewAllButton ||
    data?.viewTimePeriod !== config?.viewTimePeriod ||
    data?.viewTitleBar !== config?.viewTitleBar ||
    data?.selectedTimePeriod !== config?.selectedTimePeriod ||
    data?.type !== config?.type;
const checkWeatherPrefs = (data: any, config: any): boolean =>
    data?.selectedTimePeriod !== config?.selectedTimePeriod ||
    data?.displayLastUpdate !== config?.displayLastUpdate ||
    data?.displayTitleBar !== config?.displayTitleBar ||
    data?.displayViewBar !== config?.displayViewBar ||
    data?.displayForecastPeriod !== config?.displayForecastPeriod;
const checkConsumptionBreakdownPrefs = (data: any, config: any): boolean =>
    data?.maxCount !== config?.maxCount ||
    data?.isDataSortDesc !== config?.isDataSortDesc ||
    data?.selectedTimePeriod !== config?.selectedTimePeriod ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.viewTimePeriod !== config?.viewTimePeriod ||
    data?.viewTitleBar !== config?.viewTitleBar ||
    data?.type !== config?.type ||
    data?.assetTypes?.length !== config?.assetTypes?.length ||
    !data?.assetTypes?.every?.((item: any) =>
        config?.assetTypes?.find((subItem: any) => subItem?.deviceCategory === item?.deviceCategory)
    ) ||
    !data?.assetTypes?.every?.((item: any) =>
        config?.assetTypes?.find((subItem: any) => subItem?.isEnabled === item?.isEnabled)
    );
const checkEnergyFlowPrefs = (data: any, config: any): boolean =>
    data?.isAssetsTypeDisplayed !== config?.isAssetsTypeDisplayed ||
    data?.isTitleDisplayed !== config?.isTitleDisplayed ||
    data?.isViewAllDisplayed !== config?.isViewAllDisplayed ||
    data?.assetsCount !== config?.assetsCount ||
    data?.assetTypes?.primary?.length !== config?.assetTypes?.primary?.length ||
    data?.assetTypes?.secondary?.length !== config?.assetTypes?.secondary?.length ||
    !data?.assetTypes?.primary?.every((item: any) =>
        config?.assetTypes?.primary?.find((subItem: any) => subItem?.deviceCategory === item?.deviceCategory)
    ) ||
    !data?.assetTypes?.secondary?.every((item: any) =>
        config?.assetTypes?.secondary?.find((subItem: any) => subItem?.deviceCategory === item?.deviceCategory)
    ) ||
    !data?.assetTypes?.primary?.every((item: any) =>
        config?.assetTypes?.primary?.find((subItem: any) => subItem?.isEnabled === item?.isEnabled)
    ) ||
    !data?.assetTypes?.secondary?.every((item: any) =>
        config?.assetTypes?.secondary?.find((subItem: any) => subItem?.isEnabled === item?.isEnabled)
    );
const checkChartsTrendsPrefs = (data: any, config: any): boolean =>
    data?.maxCount !== config?.maxCount || data?.type !== config?.type || data?.duration !== config?.duration;
const checkLoadGroupTrendsPrefs = (data: any, config: any): boolean =>
    data?.isAutomated !== config?.isAutomated ||
    data?.maxCount !== config?.maxCount ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    data?.countMode !== config?.countMode ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.duration !== (config?.duration ?? 24) ||
    data?.type !== (config?.type ?? 'area');
const checkLoadGroupsLoadsPrefs = (data: any, config: any): boolean =>
    data?.isAutomated !== config?.isAutomated ||
    data?.maxCount !== config?.maxCount ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    data?.countMode !== config?.countMode ||
    data?.viewAllButton !== config?.viewAllButton;
const checkGaugePrefs = (data: any, config: any): boolean =>
    data?.viewScale !== config?.viewScale ||
    data?.type !== config?.type ||
    data?.viewThreshold !== config?.viewThreshold;
const checkDetailsPrefs = (data: any, config: any): boolean =>
    data?.viewAllButton !== config?.viewAllButton ||
    data?.secondaryChannelCount !== config?.secondaryChannelCount ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    data?.showLastUpdateDate !== config?.showLastUpdateDate ||
    data?.showWidgetTitleBar !== config?.showWidgetTitleBar;
const checkSettingsPropertiesPrefs = (data: any, config: any): boolean =>
    data?.showWidgetTitleBar !== config?.showWidgetTitleBar ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.secondaryChannelCount !== config?.secondaryChannelCount ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    data?.countMode !== config?.countMode;
const checkMapPrefs = (data: any, config: any, isDiff: (key: string) => boolean): boolean =>
    data?.viewAllButton !== config?.viewAllButton ||
    data?.isPinned !== config?.isPinned ||
    data?.showPinLabels !== config?.showPinLabels ||
    data?.showTouchTooltipsMobile !== config?.showTouchTooltipsMobile ||
    isDiff('deviceStatus') ||
    isDiff('modelCategory');
const checkGroupListDeviceListPrefs = (data: any, config: any): boolean =>
    data?.countMode !== config?.countMode ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.maxCount !== config?.maxCount ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    JSON.stringify(data?.sortPayload) !== JSON.stringify(config?.sortPayload);
const checkTimelinePrefs = (data: any, config: any): boolean =>
    data?.showSeverityCount !== config?.showSeverityCount ||
    data?.maxCount !== config?.maxCount ||
    config?.preferences?.length !== data?.preferences?.length ||
    !config?.preferences?.every((item: any) => data?.preferences?.includes(item));
const checkCommandBarPrefs = (data: any, config: any): boolean =>
    data?.isPinned !== config?.isPinned ||
    data?.viewTitleBar !== config?.viewTitleBar ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible;
const checkVirtualLoadsPrefs = (data: any, config: any): boolean =>
    data?.viewAllButton !== config?.viewAllButton ||
    data?.isTotalCountVisible !== config?.isTotalCountVisible ||
    data?.maxCount !== config?.maxCount;
const checkScheduleWidgetPrefs = (data: any, config: any): boolean =>
    data?.viewTitleBar !== config?.viewTitleBar ||
    data?.viewAllButton !== config?.viewAllButton ||
    data?.maxCount !== config?.maxCount;
const hasPreferenceKeyDifference = (data: any, config: any, keys: string[]): boolean =>
    keys.some((key: string) => data?.[key] !== config?.[key]);

const checkUpcomingComponentsPrefs = (data: any, config: any): boolean =>
    hasPreferenceKeyDifference(data, config, [
        'countMode',
        'viewTitleBar',
        'viewAllButton',
        'isTotalCountVisible',
        'maxCount',
    ]);
const checkScorePrefs = (data: any, config: any): boolean =>
    data?.isDescriptionVisible !== config?.isDescriptionVisible ||
    data?.isLearnMoreVisible !== config?.isLearnMoreVisible;
const checkEnvironmentalStressMonitoringPrefs = (data: any, config: any): boolean =>
    hasPreferenceKeyDifference(data, config, ['maxCount', 'viewAllButton']);
const prefCheckersByWidgetType: Record<string, (d: any, c: any) => boolean> = {
    aggregated_trends: checkAggregatedTrendsPrefs,
    load_trends: checkLoadTrendsPrefs,
    weather: checkWeatherPrefs,
    consumption_breakdown: checkConsumptionBreakdownPrefs,
    energy_flow: checkEnergyFlowPrefs,
    charts: checkChartsTrendsPrefs,
    trends: checkChartsTrendsPrefs,
    load_group_trends: checkLoadGroupTrendsPrefs,
    load_groups: checkLoadGroupsLoadsPrefs,
    loads: checkLoadGroupsLoadsPrefs,
    gauge: checkGaugePrefs,
    details: checkDetailsPrefs,
    settings: checkSettingsPropertiesPrefs,
    properties: checkSettingsPropertiesPrefs,
    group_list: checkGroupListDeviceListPrefs,
    device_list: checkGroupListDeviceListPrefs,
    timeline: checkTimelinePrefs,
    command_bar: checkCommandBarPrefs,
    virtual_loads: checkVirtualLoadsPrefs,
    schedule: checkScheduleWidgetPrefs,
    upcoming: checkUpcomingComponentsPrefs,
    environmental_stress_monitoring: checkEnvironmentalStressMonitoringPrefs,
    components: checkUpcomingComponentsPrefs,
    score: checkScorePrefs,
};

const getSafeObject = (value: any): Record<string, any> => (value && typeof value === 'object' ? value : {});

const getSafeArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const isUpcomingChanged = (
    widgetDetails: any,
    configurationPayload: any,
    preferenceHelper: () => boolean,
    combineArraysFromKeys: (keys: string[], filters: any) => any[]
): boolean => {
    const config = getSafeObject(getSafeObject(widgetDetails).config);
    const filters = getSafeObject(config.filters);
    const data = getSafeObject(getSafeObject(configurationPayload).data);
    const selectedNodes = getSafeArray(data.selectedNodes);
    const hasFilters = Boolean(filters && Object.keys(filters).length);

    if (Boolean(config.multiDevice) !== Boolean(data.multiDevice)) {
        return true;
    }

    if (preferenceHelper()) {
        return true;
    }

    if (!data.multiDevice) {
        return false;
    }

    if (!hasFilters) {
        return Boolean(selectedNodes.length);
    }

    const combinedFilterArray = combineArraysFromKeys(['groupId', 'deviceIds', 'deviceId', 'gatewayId'], filters);

    if (selectedNodes.length !== combinedFilterArray.length) {
        return true;
    }

    return !selectedNodes.every((item: any) => combinedFilterArray.includes(item?.id));
};

export const WidgetConfigurationHeader = (): JSX.Element => {
    const saveAsModalRef = React.useRef<any>(null);
    const saveModalRef = React.useRef<any>(null);
    const theme: any = useTheme();
    const configurationPayload = useTypedSelector((state) => state['widgetConfiguration']);
    const configurationLoader = useTypedSelector((data) => data.widgetConfiguration?.['isLoading']);
    const {
        handleSaveButton,
        handleCloseButton,
        updateLoader,
        details: { widgetDetails },
    } = useWidgetConfigHandlers();

    const { isVisible, onClick, onCancel, onConfirm } = useConfirm(handleCloseButton);

    const selectedSource = useTypedSelector((state) => state.widgetConfiguration.source);

    const modalActions: ModalActions[] = [
        {
            key: '#exit',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            variant: 'outlined',
            close: true,
            handleClick: onCancel,
        },
        {
            key: '#closeEditor',
            label: <CustomTransComponent translationKey={'WIDGETS:CLOSE_WARN_MODAL.CLOSE_EDITOR'} />,
            variant: 'contained',
            handleClick: (): void => {
                onConfirm();
            },
        },
    ];

    const combineArraysFromKeys = (keys: string[], obj: any): any =>
        keys.reduce((result: any, key: any) => {
            if (Array.isArray(obj?.[key])) {
                result.push(...obj[key]);
            }
            return result;
        }, []);

    const isChannelPresent = (channelData: any, alreadyPresentChannel: any, widgetType?: any): any => {
        if (widgetType === 'command_bar') {
            return channelData?.channels?.every((channel: any) =>
                alreadyPresentChannel?.channels?.find(
                    (subChannel: any) =>
                        subChannel?.channelId === channel?.channelId &&
                        subChannel?.paletteConfig?.type === channel?.paletteConfig?.type &&
                        subChannel?.paletteConfig?.color === channel?.paletteConfig?.color &&
                        subChannel?.isConfirm === channel?.isConfirm
                )
            );
        }
        return channelData?.channels?.every((channel: any) =>
            alreadyPresentChannel?.channels?.find((subChannel: any) => subChannel?.channelId === channel?.channelId)
        );
    };

    const isDeviceAlreadyPresent = (channelData: any, key: any, widgetType?: any): any => {
        const alreadyPresentChannel = configurationPayload?.data?.[key]?.find(
            (item: any) => item?.deviceId === channelData?.deviceId
        );
        return (
            alreadyPresentChannel &&
            isChannelPresent(channelData, alreadyPresentChannel, widgetType) &&
            alreadyPresentChannel?.channels?.length === channelData?.channels?.length
        );
    };

    const defaultConfigHelper = (key: any, widgetType?: string): boolean =>
        !widgetDetails?.config?.[key]?.every((channelData: any) =>
            isDeviceAlreadyPresent(channelData, key, widgetType)
        );

    const isDifferent = (key: string): boolean => {
        const filterA = widgetDetails?.config?.filters?.[key];
        const filterB = configurationPayload?.data?.filters?.[key];
        if (filterA?.length !== filterB?.length) {
            return true;
        }
        return filterA && filterB ? !filterA.every((item: any) => filterB.includes(item)) : false;
    };

    const preferenceHelper = (): boolean => {
        const widgetTypeId = widgetDetails?.widgetType?.id;
        const data = configurationPayload?.data;
        const config = widgetDetails?.config;
        if (widgetTypeId === 'map') return checkMapPrefs(data, config, isDifferent);
        const checker = prefCheckersByWidgetType[widgetTypeId];
        return checker ? checker(data, config) : false;
    };

    const chartConfigHandler = (): boolean => {
        // Check if channels exist and have same length
        if (!widgetDetails?.config?.secondary || !configurationPayload?.data?.secondary) {
            return false;
        }

        const originalChannels = widgetDetails?.config?.secondary;
        const currentChannels = configurationPayload?.data?.secondary;

        // Check if different number of channels
        if (originalChannels.length !== currentChannels.length) {
            return true;
        }

        // Check if channels exist in different order or are different
        return !originalChannels.every((channel: any, index: number) => {
            const currentChannel = currentChannels[index];
            if (configurationPayload?.data?.multiDevice) {
                return (
                    currentChannel &&
                    channel?.channelId === currentChannel?.channelId &&
                    channel?.deviceId === currentChannel?.deviceId
                );
            }
            return currentChannel && channel?.channelId === currentChannel?.channelId;
        });
    };

    const hasOrderChanged = (item1: any[] = [], item2: any[] = []): boolean => {
        if (item1?.length !== item2?.length) return true;

        return item1?.some((item, index) => {
            const { channelId: currentChannelId, channels: currentChannels } = item ?? {};
            const { channelId: otherChannelId, channels: otherChannels } = item2?.[index] ?? {};

            if (currentChannelId && otherChannelId && currentChannelId !== otherChannelId) {
                return true;
            }

            return currentChannels ? hasOrderChanged(currentChannels, otherChannels) : false;
        });
    };

    const isSecondaryOrderChanged = (): boolean => {
        const originalSecondary = widgetDetails?.config?.secondary || [];
        const currentSecondary = configurationPayload?.data?.secondary || [];

        // If lengths are different, order has definitely changed
        if (originalSecondary.length !== currentSecondary.length) {
            return true;
        }

        // Check if the order of channelIds is the same
        return !originalSecondary.every((channel: any, index: number) => {
            const currentChannel = currentSecondary[index];
            if (configurationPayload?.data?.multiDevice) {
                return (
                    currentChannel &&
                    channel?.channelId === currentChannel?.channelId &&
                    channel?.deviceId === currentChannel?.deviceId
                );
            }
            return currentChannel && channel?.channelId === currentChannel?.channelId;
        });
    };

    const isDetailsConfigChanged = (): any => {
        const { primary: originalPrimary, secondary: originalSecondary } = widgetDetails?.config ?? {};
        const { primary: currentPrimary, secondary: currentSecondary, multiDevice } = configurationPayload?.data ?? {};
        const hasAnyChannels = Boolean(currentSecondary?.length || currentPrimary?.length);

        return originalPrimary || originalSecondary
            ? (multiDevice && defaultConfigHelper('primary')) ||
                  (originalSecondary && multiDevice && defaultConfigHelper('secondary')) ||
                  (originalSecondary && originalSecondary?.length !== currentSecondary?.length) ||
                  originalPrimary?.length !== currentPrimary?.length ||
                  preferenceHelper() ||
                  hasOrderChanged(originalSecondary, currentSecondary) ||
                  hasOrderChanged(originalPrimary, currentPrimary)
            : preferenceHelper() || hasAnyChannels;
    };
    const isSettingsPropertiesCommandConfigChanged = (type?: string): any =>
        widgetDetails?.config?.secondary
            ? (defaultConfigHelper('secondary', type) && configurationPayload?.data?.multiDevice) ||
              widgetDetails?.config?.secondary?.length !== configurationPayload?.data?.secondary?.length ||
              preferenceHelper() ||
              hasOrderChanged(widgetDetails?.config?.secondary, configurationPayload?.data?.secondary)
            : configurationPayload?.data?.secondary?.length || preferenceHelper();

    const isTrendsChartsConfigChanged = (): any =>
        widgetDetails?.config?.secondary
            ? hasSourceChanged(widgetDetails?.sourceId, selectedSource?.id) ||
              chartConfigHandler() ||
              isSecondaryOrderChanged() ||
              preferenceHelper()
            : configurationPayload?.data?.secondary?.length || preferenceHelper();

    const isGaugeConfigChanged = (): any =>
        widgetDetails?.config?.data
            ? widgetDetails?.config?.data?.channelId !== configurationPayload?.data?.data?.channelId ||
              preferenceHelper() ||
              widgetDetails?.config?.multiDevice !== configurationPayload?.data?.multiDevice
            : configurationPayload?.data?.data?.channelId || preferenceHelper();

    const isLoadsConfigChanged = (): any =>
        widgetDetails?.config?.selectedNodes && configurationPayload?.data?.multiDevice
            ? !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  widgetDetails?.config?.selectedNodes?.find(
                      (subItem: any) => subItem?.loadId === item?.loadId && item?.deviceId === subItem?.deviceId
                  )
              ) ||
              preferenceHelper() ||
              configurationPayload?.data?.selectedNodes?.length !== widgetDetails?.config?.selectedNodes?.length
            : widgetDetails?.config?.secondary &&
              !widgetDetails?.config?.multiDevice &&
              !configurationPayload?.data?.isAutomated
            ? widgetDetails?.config?.secondary?.length !== configurationPayload?.data?.secondary?.length ||
              !configurationPayload?.data?.secondary?.every((item: any) =>
                  widgetDetails?.config?.secondary?.includes(item?.loadId)
              ) ||
              preferenceHelper()
            : preferenceHelper() ||
              (configurationPayload?.data?.secondary?.length && !configurationPayload?.data?.isAutomated);

    const isLoadGroupsConfigChanged = (): any =>
        widgetDetails?.config?.selectedNodes && configurationPayload?.data?.multiDevice
            ? !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  widgetDetails?.config?.selectedNodes?.find((subItem: any) => subItem?.id === item?.id)
              ) ||
              preferenceHelper() ||
              configurationPayload?.data?.selectedNodes?.length !== widgetDetails?.config?.selectedNodes?.length
            : widgetDetails?.config?.secondary &&
              !widgetDetails?.config?.multiDevice &&
              !configurationPayload?.data?.isAutomated
            ? widgetDetails?.config?.secondary?.length !== configurationPayload?.data?.secondary?.length ||
              !configurationPayload?.data?.secondary?.every((item: any) =>
                  widgetDetails?.config?.secondary?.find((subItem: any) => subItem?.id === item?.id)
              ) ||
              preferenceHelper() ||
              hasSourceChanged(widgetDetails?.sourceId, configurationPayload?.source?.id)
            : preferenceHelper() ||
              hasSourceChanged(widgetDetails?.sourceId, configurationPayload?.source?.id) ||
              (configurationPayload?.data?.secondary?.length && !configurationPayload?.data?.isAutomated);

    const isGroupListConfigChanged = (): any =>
        widgetDetails?.config?.filters && Object.keys(widgetDetails?.config?.filters)?.length
            ? preferenceHelper() ||
              !configurationPayload?.data?.multiDevice ||
              !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  combineArraysFromKeys(['groupIds', 'gatewayId'], widgetDetails?.config?.filters)?.find(
                      (subItem: any) => subItem === item?.id
                  )
              ) ||
              configurationPayload?.data?.selectedNodes?.length !==
                  combineArraysFromKeys(['groupIds', 'gatewayId'], widgetDetails?.config?.filters).length
            : preferenceHelper() || configurationPayload?.data?.selectedNodes?.length;

    const isScheduleConfigChanged = (): any =>
        widgetDetails?.config?.filters && Object.keys(widgetDetails?.config?.filters)?.length
            ? preferenceHelper() ||
              !configurationPayload?.data?.multiDevice ||
              !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  combineArraysFromKeys(['deviceId'], widgetDetails?.config?.filters)?.find(
                      (subItem: any) => subItem === item?.id
                  )
              ) ||
              configurationPayload?.data?.selectedNodes?.length !==
                  combineArraysFromKeys(['deviceId'], widgetDetails?.config?.filters).length
            : preferenceHelper() || configurationPayload?.data?.selectedNodes?.length;

    const isDeviceListConfigChanged = (): any =>
        widgetDetails?.config?.filters && Object.keys(widgetDetails?.config?.filters)?.length
            ? preferenceHelper() ||
              !configurationPayload?.data?.multiDevice ||
              !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  combineArraysFromKeys(['deviceIds'], widgetDetails?.config?.filters)?.find(
                      (subItem: any) => subItem === item?.id
                  )
              ) ||
              configurationPayload?.data?.selectedNodes?.length !==
                  combineArraysFromKeys(['deviceIds'], widgetDetails?.config?.filters).length
            : preferenceHelper() || configurationPayload?.data?.selectedNodes?.length;

    const isTimelineConfigChanged = (): any =>
        widgetDetails?.config?.filters && Object.keys(widgetDetails?.config?.filters)?.length
            ? preferenceHelper() ||
              !configurationPayload?.data?.multiDevice ||
              !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  combineArraysFromKeys(['groupId', 'deviceId', 'gatewayId'], widgetDetails?.config?.filters)?.find(
                      (subItem: any) => subItem === item?.id
                  )
              ) ||
              configurationPayload?.data?.selectedNodes?.length !==
                  combineArraysFromKeys(['deviceId', 'groupId', 'gatewayId'], widgetDetails?.config?.filters).length
            : preferenceHelper() || configurationPayload?.data?.selectedNodes?.length;

    const isMapConfigChanged = (): any =>
        widgetDetails?.config?.filters &&
        Object.keys(widgetDetails?.config?.filters)?.length &&
        configurationPayload?.data?.multiDevice
            ? preferenceHelper() ||
              !configurationPayload?.data?.multiDevice ||
              !configurationPayload?.data?.selectedNodes?.every((item: any) =>
                  combineArraysFromKeys(['groups', 'deviceIds', 'parentId'], widgetDetails?.config?.filters)?.find(
                      (subItem: any) => subItem === item?.id
                  )
              ) ||
              configurationPayload?.data?.selectedNodes?.length !==
                  combineArraysFromKeys(['deviceIds', 'groups', 'parentId'], widgetDetails?.config?.filters).length
            : preferenceHelper() ||
              configurationPayload?.data?.selectedNodes?.length ||
              widgetDetails?.config?.multiDevice !== configurationPayload?.data?.multiDevice;

    const isEnergyFlowConfigChanged = (): any =>
        widgetDetails?.config?.assets?.length
            ? widgetDetails?.config?.assets?.length !== configurationPayload?.data?.assets?.length ||
              preferenceHelper() ||
              !configurationPayload?.data?.assets?.every((item: any) =>
                  widgetDetails?.config?.assets?.find((subItem: any) => item?.id === subItem?.id)
              )
            : preferenceHelper() || configurationPayload?.data?.assets?.length;

    const isWeatherConfigChanged = (): any =>
        Object.keys(widgetDetails?.config?.assets ?? {})?.length
            ? widgetDetails?.config?.assets?.id !== configurationPayload?.data?.assets?.id || preferenceHelper()
            : preferenceHelper();

    const isConfigChanged = (): boolean => {
        const widgetTypeId = widgetDetails?.widgetType?.id;
        const settingsTypes: Record<string, boolean> = { settings: true, properties: true, command_bar: true };
        const upcomingTypes: Record<string, boolean> = {
            score: true,
            upcoming: true,
            components: true,
            environmental_stress_monitoring: true,
        };
        const configHandlers: Record<string, () => any> = {
            load_trends: isEnergyFlowConfigChanged,
            energy_flow: isEnergyFlowConfigChanged,
            consumption_breakdown: isEnergyFlowConfigChanged,
            weather: isWeatherConfigChanged,
            aggregated_trends: isEnergyFlowConfigChanged,
            details: isDetailsConfigChanged,
            trends: isTrendsChartsConfigChanged,
            charts: isTrendsChartsConfigChanged,
            gauge: isGaugeConfigChanged,
            load_groups: isLoadGroupsConfigChanged,
            load_group_trends: isLoadGroupsConfigChanged,
            loads: isLoadsConfigChanged,
            group_list: isGroupListConfigChanged,
            device_list: isDeviceListConfigChanged,
            timeline: isTimelineConfigChanged,
            map: isMapConfigChanged,
            virtual_loads: preferenceHelper,
            schedule: isScheduleConfigChanged,
        };
        if (settingsTypes[widgetTypeId]) return isSettingsPropertiesCommandConfigChanged(widgetTypeId);
        if (upcomingTypes[widgetTypeId])
            return isUpcomingChanged(widgetDetails, configurationPayload, preferenceHelper, combineArraysFromKeys);
        const handler = configHandlers[widgetTypeId];
        if (handler) return handler();
        // Custom widgets: Always keep Save button disabled
        if (widgetDetails?.widgetType?.type === 'CUSTOM') return false;
        // For other widget types not explicitly handled above:
        // Only enable Save button if user has made actual configuration changes
        // This prevents saving empty/incomplete widgets on Widget Builder page
        return (
            configurationPayload?.data &&
            (configurationPayload?.data?.selectedNodes?.length > 0 ||
                configurationPayload?.data?.secondary?.length > 0 ||
                configurationPayload?.data?.primary?.length > 0 ||
                configurationPayload?.data?.assets?.length > 0 ||
                Object.keys(configurationPayload?.data?.filters || {}).length > 0)
        );
    };

    return (
        <>
            <AppBar position={'static'} data-testid="widget-config-header">
                <Toolbar className="bg-white text-black padding-5">
                    <Stack className="w-100">
                        <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                            <Stack direction={'row'} justifyContent={'flex-end'} spacing={1} width={'100%'}>
                                <Button
                                    data-testid="widget-header-close-config-button"
                                    sx={{
                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                        '&:hover': {
                                            backgroundColor: theme?.palette?.primary?.[50],
                                        },
                                    }}
                                    variant="outlined"
                                    onClick={isConfigChanged() ? onClick : handleCloseButton}
                                >
                                    {<CustomTransComponent translationKey={'COMMON:CLOSE'} />}
                                </Button>
                                <Button
                                    disabled={configurationLoader || updateLoader}
                                    data-testid="widget-header-save-as-button"
                                    sx={{
                                        border: `1px solid ${theme?.palette?.primary?.main}`,
                                        '&:hover': {
                                            backgroundColor: theme?.palette?.primary?.[50],
                                        },
                                    }}
                                    variant="outlined"
                                    onClick={(): void => {
                                        saveAsModalRef?.current?.handleModalAction(true, {
                                            ...widgetDetails,
                                            config: configurationPayload?.data,
                                        });
                                    }}
                                >
                                    <CustomTransComponent translationKey={'WIDGETS:SAVE_AS_LABEL'} />
                                </Button>
                                <Button
                                    data-testid="widget-header-save-button"
                                    sx={{
                                        backgroundColor: theme?.palette?.primary?.main,
                                        '&:hover': {
                                            backgroundColor: theme?.palette?.primary?.main,
                                        },
                                        '&.Mui-disabled': {
                                            color: theme?.palette?.primary?.[200],
                                            backgroundColor: theme?.palette?.primary?.[50],
                                        },
                                    }}
                                    {...(updateLoader && {
                                        startIcon: <CircularProgress color="inherit" size={20} />,
                                    })}
                                    variant="contained"
                                    disabled={!isConfigChanged() || configurationLoader || updateLoader}
                                    onClick={() =>
                                        getWidgetTypeValidationConfig(widgetDetails?.widgetType?.id)
                                            ?.isSourceRequired &&
                                        hasSourceChanged(widgetDetails?.sourceId, selectedSource?.id)
                                            ? saveModalRef?.current?.openSaveModal()
                                            : handleSaveButton()
                                    }
                                >
                                    {updateLoader ? (
                                        'Saving...'
                                    ) : (
                                        <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />
                                    )}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>

            <CustomModal
                data-testid="widget-header-close-modal"
                key="#closeModal"
                actions={modalActions}
                isOpen={isVisible}
                type="primary"
                actionsDivider
                header={<CustomTransComponent translationKey={'WIDGETS:CLOSE_WARN_MODAL.HEADER'} />}
            >
                <Typography variant="subtitle2">
                    <CustomTransComponent translationKey={'WIDGETS:CLOSE_WARN_MODAL.DESCRIPTION'} />
                </Typography>
            </CustomModal>

            <CloneWidgetModal data-testid="widget-header-clone-widget-modal" ref={saveAsModalRef} modalType="save" />

            <SaveOverrideModal data-testid="widget-header-save-override-modal" key="ssve1d" ref={saveModalRef} />
        </>
    );
};
